import React, { Component } from 'react';
import PropTypes from 'prop-types'
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actions as userActions } from '../../actions/userActions';
import { actions as locationActions } from '../../actions/locationActions';
import * as types from '../../constants/actionTypes';
import Drawer from '../Common/Drawer/Drawer';
import InviteUserForm from './InviteUserForm';
import LoadingIndicator from '../Common/LoadingIndicator/LoadingIndicator';
import Notification from '../Common/Notification';
import { convertFromRaw } from 'draft-js';
import { stateToHTML } from 'draft-js-export-html';
import { USER_LOCATION_ACCESS_OPTIONS } from '../../constants/constants';
import { isValidEmailAddress } from '../../helpers/validation';

export class InviteUserDrawer extends Component {

    constructor(props, context) {
        super(props, context);

        const newUser = {
            firstName: '',
            lastName: '',
            email: '',
            userName: '',
            jobTitle: '',
            workPhone: '',
            countryId: '',
            roleId: '',
            managementCompanyId: '',
            communities: '',
            departments: '',
            excludeAsProjectCoach: false,
            allowTaskListExportToExcel: false,
            allowLocationTaskDateChanges: false,
            allowLocationTaskBudgetEntry: false,
            ssoUser: false,
            selectedUserLocationAccess: USER_LOCATION_ACCESS_OPTIONS.NO.value,
            userLocationRules: {
                areas: '',
                brands: '',
                continents: '',
                countries: '',
                locationTypes: '',
                managementCompanies: '',
                regions: ''
            }
        };

        this.state = {
            user: newUser,
            isFormValid: true,
            isUserLocationAccessFormValid: false,
            hasEmailBeenModified: false,
            isSubscriptionProjectEnabled: false,
            isSubscriptionSsoEnabled: false,
            isSubscriptionTaskFinancialsActive: false,
            countries: [],
            roles: [],
            managementCompanies: [],
            communities: [],
            departments: [],
            onFormChange: [],
            inviteMessageJSON: '',
            isLoading: false
        };
    }

    componentDidMount() {
        this.loadFilters();
    }

    onEmailChange = (email) => {
        /**
         * If userName does not have any value, then automatically put whatever
         * is being typed in email into the userName field as well, to default
         * it being the same as email. If userName DOES have a value, then only
         * update the email field value.
         */
        if (!this.state.hasEmailBeenModified) {
            this.setState({
                user: {
                    ...this.state.user,
                    email: email,
                    userName: email
                }
            });
        } else {
            this.setState({ user: { ...this.state.user, email: email } });
        }
    }

    onEmailBlur = () => {
        this.setState({ hasEmailBeenModified: true });
    }

    onFormChange = (name, value) => {
        this.setState({ user: { ...this.state.user, [name]: value } });
    }

    onUserLocationAccessFormChange = (name, value) => {
        const user = {
            ...this.state.user,
            userLocationRules: {
                ...this.state.user.userLocationRules,
                [name]: value
            }
        }
        const isUserLocationAccessFormValid = this.validateUserLocationAccessRules(user);
        this.setState({
            user: user,
            isUserLocationAccessFormValid: isUserLocationAccessFormValid
        });
    }

    onCancel = () => {
        this.setState({ isFormValid: true, drawerOpen: false });
        this.props.onCancel();
    }

    loadFilters = async () => {
        try {
            this.setState({ isLoading: true });
            const response = await this.props.userActions.loadInviteUserFilters(this.props.subscriptionId);
            if (response.type === types.LOAD_INVITE_USER_FILTERS_SUCCESS) {
                this.setState({
                    isSubscriptionProjectEnabled: response.payload.isSubscriptionProjectEnabled,
                    isSubscriptionSsoEnabled: response.payload.isSubscriptionSsoEnabled,
                    isSubscriptionTaskFinancialsActive: response.payload.isSubscriptionTaskFinancialsActive,
                    communities: response.payload.communities,
                    roles: response.payload.roles,
                    managementCompanies: response.payload.managementCompanies,
                    departments: response.payload.departments,
                    countries: response.payload.countries,
                    inviteMessageJSON: response.payload.inviteMessage
                });

                if (!response.payload.inviteMessage) {
                    Notification("error", "Warning", "This subscription has not defined a New User Invitation Email Message, which is required to invite new users");
                }
            } else if (response.type === types.LOAD_INVITE_USER_FILTERS_FAILURE) {
                Notification("error", "An Error Occurred", response.payload.message);
            }
        } catch (error) {
            Notification("error", "Error", error);
        } finally {
            this.setState({ isLoading: false });
        }
    }

    validateUserLocationAccessRules = (user) => {
        switch (user.selectedUserLocationAccess) {
            case USER_LOCATION_ACCESS_OPTIONS.YES.value:
            case USER_LOCATION_ACCESS_OPTIONS.NO.value: {
                return true;
            }
            case USER_LOCATION_ACCESS_OPTIONS.RULES.value: {
                const {
                    areas,
                    brands,
                    continents,
                    countries,
                    locationTypes,
                    managementCompanies,
                    regions
                } = user.userLocationRules;

                // Consider the form valid is at least one of the multi-selects has at least one value,
                // or rather, consider the form invalid if NONE of them have ANY values
                if (areas.trim().length <= 0
                    && brands.trim().length <= 0
                    && continents.trim().length <= 0
                    && countries.trim().length <= 0
                    && locationTypes.trim().length <= 0
                    && managementCompanies.trim().length <= 0
                    && regions.trim().length <= 0) {
                    return false;
                }
                return true;
            }
            default: {
                return false;
            }
        }
    }

    validateForm = (user) => {
        let { firstName, lastName, email, userName, jobTitle, countryId, roleId, managementCompanyId, communities } = user;
        const hasValidUserLocationAccessRules = this.validateUserLocationAccessRules(user);

        email = email?.trim();

        if (!(email && email.length > 0 && isValidEmailAddress(email))) {
            Notification("error", 'Email address is not valid', '');
            return false;
        }

        if (firstName && firstName.trim().length > 0
            && lastName && lastName.trim().length > 0
            && userName && userName.trim().length > 0
            && jobTitle && jobTitle.trim().length > 0
            && roleId
            && countryId
            && managementCompanyId
            && communities && communities.trim().length > 0
            && hasValidUserLocationAccessRules
        ) {
            return true;
        }

        return false;
    }

    onSave = async () => {
        try {
            const userNameIsEmail = this.state.user.userName === this.state.user.email;
            const trimmedEmail = this.state.user.email.trim();

            const userWithTrimmedEmail = {
                ...this.state.user,
                email: trimmedEmail,
                userName: userNameIsEmail ? trimmedEmail : this.state.user.userName
            };

            if (this.validateForm(userWithTrimmedEmail)) {
                this.setState({
                    isFormValid: true,
                    isUserLocationAccessFormValid: true,
                    isLoading: true,
                    user: userWithTrimmedEmail
                });

                let communityIds = [];
                if (userWithTrimmedEmail.communities?.trim().length > 0) {
                    communityIds = userWithTrimmedEmail.communities.split(',');
                }

                let departmentIds = [];
                if (userWithTrimmedEmail.departments?.trim().length > 0) {
                    departmentIds = userWithTrimmedEmail.departments.split(',');
                }

                let userLocationRules = {
                    areaIds: [],
                    brandIds: [],
                    continentIds: [],
                    countryIds: [],
                    locationTypeIds: [],
                    managementCompanyIds: [],
                    regionIds: []
                };

                let grantAccessToAllLocations = false;
                let showLocationAccessDrawerAfterSave = false;

                if (userWithTrimmedEmail.selectedUserLocationAccess === USER_LOCATION_ACCESS_OPTIONS.RULES.value) {
                    userLocationRules = this.parseUserLocationRules(userLocationRules);
                    grantAccessToAllLocations = null;
                } else if (userWithTrimmedEmail.selectedUserLocationAccess === USER_LOCATION_ACCESS_OPTIONS.YES.value) {
                    grantAccessToAllLocations = true;
                } else {
                    showLocationAccessDrawerAfterSave = true;
                }

                const jsonContent = JSON.parse(this.state.inviteMessageJSON);
                const contentState = convertFromRaw(jsonContent);
                const inviteMessageHTML = stateToHTML(contentState);

                const request = {
                    ...userWithTrimmedEmail,
                    userName: userWithTrimmedEmail.userName.trim(),
                    subscriptionId: this.props.subscriptionId,
                    communityIds: communityIds,
                    departmentIds: departmentIds,
                    inviteMessageHTML: inviteMessageHTML,
                    areaIds: userLocationRules.areaIds,
                    brandIds: userLocationRules.brandIds,
                    continentIds: userLocationRules.continentIds,
                    countryIds: userLocationRules.countryIds,
                    locationTypeIds: userLocationRules.locationTypeIds,
                    managementCompanyIds: userLocationRules.managementCompanyIds,
                    regionIds: userLocationRules.regionIds,
                    grantAccessToAllLocations: grantAccessToAllLocations
                };

                const result = await this.props.userActions.inviteUser(request);
                if (result.type === types.INVITE_USER_SUCCESS) {
                    if (result.payload.emailSent) {
                        Notification("success", "Success", "User invited successfully");
                    } else {
                        Notification("warning", "Warning", "User invited successfully but invitation email failed to send");
                    }
                    this.setState({ isFormValid: true, drawerOpen: false });
                    this.props.onComplete(showLocationAccessDrawerAfterSave, result.payload.newUserId);
                } else if (result.type === types.INVITE_USER_FAILURE) {
                    Notification("error", "Error", result.payload.message);
                    this.setState({ isLoading: false });
                }
            } else {
                this.setState({ isFormValid: false, isLoading: false });
            }
        } catch (error) {
            Notification("error", "Error", error);
            this.setState({ isLoading: false });
        }
    };

    parseUserLocationRules = (rules) => {
        if (this.state.user.userLocationRules.areas.trim().length > 0) {
            rules.areaIds = this.state.user.userLocationRules.areas.split(',');
        }

        if (this.state.user.userLocationRules.brands.trim().length > 0) {
            rules.brandIds = this.state.user.userLocationRules.brands.split(',');
        }

        if (this.state.user.userLocationRules.continents.trim().length > 0) {
            rules.continentIds = this.state.user.userLocationRules.continents.split(',');
        }

        if (this.state.user.userLocationRules.countries.trim().length > 0) {
            rules.countryIds = this.state.user.userLocationRules.countries.split(',');
        }

        if (this.state.user.userLocationRules.locationTypes.trim().length > 0) {
            rules.locationTypeIds = this.state.user.userLocationRules.locationTypes.split(',');
        }

        if (this.state.user.userLocationRules.managementCompanies.trim().length > 0) {
            rules.managementCompanyIds = this.state.user.userLocationRules.managementCompanies.split(',');
        }

        if (this.state.user.userLocationRules.regions.trim().length > 0) {
            rules.regionIds = this.state.user.userLocationRules.regions.split(',');
        }

        if (this.state.user.userLocationRules.areas.trim().length > 0) {
            rules.areaIds = this.state.user.userLocationRules.areas.split(',');
        }

        return rules;
    }

    render() {
        return (
            <Drawer isWide={true} title='Invite User'
                onSaveClick={this.onSave}
                saveText='Send Invite'
                onCancelClick={this.onCancel}>

                <LoadingIndicator isLoading={this.state.isLoading}>
                    <InviteUserForm
                        subscriptionId={this.props.subscriptionId}
                        isFormValid={this.state.isFormValid}
                        isSubscriptionProjectEnabled={this.state.isSubscriptionProjectEnabled}
                        isSubscriptionSsoEnabled={this.state.isSubscriptionSsoEnabled}
                        isSubscriptionTaskFinancialsActive = {this.state.isSubscriptionTaskFinancialsActive}
                        user={this.state.user}
                        countries={this.state.countries}
                        roles={this.state.roles}
                        managementCompanies={this.state.managementCompanies}
                        communities={this.state.communities}
                        departments={this.state.departments}
                        onFormChange={this.onFormChange}
                        onEmailChange={this.onEmailChange}
                        onEmailBlur={this.onEmailBlur}
                        onUserLocationAccessFormChange={this.onUserLocationAccessFormChange}
                        locationActions={this.props.locationActions}
                        isUserLocationAccessFormValid={this.state.isUserLocationAccessFormValid}
                    />
                </LoadingIndicator>
            </Drawer>
        )
    }
}

InviteUserDrawer.propTypes = {
    subscriptionId: PropTypes.string.isRequired,
    onComplete: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    userActions: PropTypes.object.isRequired,
    locationActions: PropTypes.object.isRequired
};

function mapDispatchToProps(dispatch) {
    return {
        userActions: bindActionCreators(userActions, dispatch),
        locationActions: bindActionCreators(locationActions, dispatch)
    };
}

export default connect(null, mapDispatchToProps)(InviteUserDrawer);
